import { type SigninRedirectArgs, User, UserManager, WebStorageStateStore } from 'oidc-client-ts'
import { useEffect, useState } from 'react'

import { wrapErrorLike } from '#features/ErrorHandling/errorHelpers'

// if this file is missing, run `npm run generate-cognito-config` to generate it
import baseConfig from './cognitoConfig.json' with { type: 'json' }

const cognitoAuthConfig = {
  ...baseConfig,
  redirect_uri: location.port
    ? `${location.protocol}//${location.hostname}:${location.port}/auth`
    : `${location.protocol}//${location.hostname}/auth`,
  post_logout_redirect_uri: location.port
    ? `${location.protocol}//${location.hostname}:${location.port}/signed-out`
    : `${location.protocol}//${location.hostname}/signed-out`,
  response_type: 'code',
  scope: 'openid profile email',
}

export const userManager = new UserManager({
  ...cognitoAuthConfig,
  loadUserInfo: true,
  stateStore: new WebStorageStateStore({
    prefix: `${VITE_APP_ID}.state`,
    store: sessionStorage,
  }),
  userStore: new WebStorageStateStore({
    prefix: `${VITE_APP_ID}.user`,
    store: localStorage,
  }),
})

export type SessionState = {
  user: User | null
  sessionStateListeners: Set<(sessionState: SessionState) => void>
  notifyListeners: () => void
  wasInitialized: boolean
}

const technicalUser = new User({
  access_token: '',
  token_type: 'Fake',
  expires_at: Number.MAX_SAFE_INTEGER,
  profile: {
    sub: '',
    iss: '',
    aud: '',
    exp: 0,
    iat: 0,
    name: 'Technical User',
  },
})

const sessionState: SessionState = {
  user: VITE_EXTERNAL_AUTH ? technicalUser : null,
  sessionStateListeners: new Set<(sessionState: SessionState) => void>(),
  notifyListeners: () => {
    for (const listener of sessionState.sessionStateListeners) {
      listener(getSessionState())
    }
  },
  wasInitialized: VITE_EXTERNAL_AUTH,
}

export async function initializeUserManager() {
  if (sessionState.wasInitialized) {
    console.warn('UserManager is already initialized.')
    return
  }

  sessionState.wasInitialized = true

  async function trySetSessionState() {
    try {
      sessionState.user = await userManager.getUser()
      sessionState.notifyListeners()
    } catch (error) {
      console.error('Error getting user:', error)
      sessionState.user = null
    }
  }

  await trySetSessionState()

  userManager.events.addUserLoaded((user: User | null) => {
    if (sessionState.user !== user) {
      sessionState.user = user
      sessionState.notifyListeners()
    }
  })
  userManager.events.addUserSessionChanged(trySetSessionState)
  userManager.events.addUserUnloaded(() => {
    if (sessionState.user) {
      sessionState.user = null
      sessionState.notifyListeners()
    }
  })
  userManager.events.addUserSignedOut(() => {
    if (sessionState.user) {
      sessionState.user = null
      sessionState.notifyListeners()
    }
  })

  userManager.startSilentRenew()
}

export function resetSessionState() {
  sessionState.user = null
  sessionState.notifyListeners()
}

export function getSessionState(): Readonly<SessionState> {
  return sessionState
}

export async function injectSession(user: User | null) {
  await userManager.storeUser(user)
  sessionState.user = user
  sessionState.notifyListeners()
}

export async function signInRedirect(args?: SigninRedirectArgs) {
  try {
    await userManager.signinRedirect(args)
  } catch (error) {
    throw wrapErrorLike(error, 'Failed to sign in')
  }
}

export async function signOutSilent() {
  try {
    await userManager.signoutSilent()
  } catch (error) {
    throw wrapErrorLike(error, 'Failed to sign out')
  }
}

export const useUserData = () => {
  const [user, setUser] = useState<User | null>(sessionState.user)

  useEffect(() => {
    async function stateChanged() {
      setUser(sessionState.user)
    }
    sessionState.sessionStateListeners.add(stateChanged)
    void stateChanged()
    return () => {
      sessionState.sessionStateListeners.delete(stateChanged)
    }
  }, [])

  return user
}

export function useIsAuthenticated() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(VITE_EXTERNAL_AUTH === true ? true : sessionState.user?.expired === false)
  useEffect(() => {
    if (VITE_EXTERNAL_AUTH === true) {
      return
    }

    async function stateChanged() {
      setIsAuthenticated(sessionState.user?.expired === false)
    }
    sessionState.sessionStateListeners.add(stateChanged)
    void stateChanged()
    return () => {
      sessionState.sessionStateListeners.delete(stateChanged)
    }
  }, [])

  return isAuthenticated
}

export function useLiveSessionExpiry(user: User | null) {
  const [expiresIn, setExpiresIn] = useState(user?.expires_in)
  useEffect(() => {
    const interval = setInterval(() => {
      if (user?.expires_in !== undefined) {
        setExpiresIn(user?.expires_in)
      }
    }, 1000)
    return () => {
      clearInterval(interval)
    }
  }, [user])

  return expiresIn
}
